/**
 * Theme context and provider
 * Manages user theme preference (light/dark/system) across the application
 */

import { createContext, useContext, useState, useEffect, useCallback } from 'react';

const ThemeContext = createContext(null);

const THEME_KEY = 'lacsched-theme-preference';

/**
 * Get the system color scheme preference
 */
function getSystemTheme() {
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return 'dark'; // Default to dark if no preference detected
}

/**
 * Theme Provider component
 */
export function ThemeProvider({ children }) {
  // 'light' | 'dark' | 'system'
  const [themePreference, setThemePreference] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem(THEME_KEY) || 'system';
    }
    return 'system';
  });

  // The actual resolved theme (always 'light' or 'dark')
  const [resolvedTheme, setResolvedTheme] = useState(() => {
    if (themePreference === 'system') {
      return getSystemTheme();
    }
    return themePreference;
  });

  // Update resolved theme when preference or system preference changes
  useEffect(() => {
    const updateResolvedTheme = () => {
      if (themePreference === 'system') {
        setResolvedTheme(getSystemTheme());
      } else {
        setResolvedTheme(themePreference);
      }
    };

    updateResolvedTheme();

    // Listen for system theme changes
    if (themePreference === 'system' && typeof window !== 'undefined') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const handler = () => updateResolvedTheme();

      mediaQuery.addEventListener('change', handler);
      return () => mediaQuery.removeEventListener('change', handler);
    }
  }, [themePreference]);

  // Persist preference to localStorage
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem(THEME_KEY, themePreference);
    }
  }, [themePreference]);

  const setTheme = useCallback((theme) => {
    if (['light', 'dark', 'system'].includes(theme)) {
      setThemePreference(theme);
    }
  }, []);

  const toggleTheme = useCallback(() => {
    setThemePreference(prev => {
      if (prev === 'light') return 'dark';
      if (prev === 'dark') return 'light';
      // If system, toggle based on current resolved theme
      return resolvedTheme === 'dark' ? 'light' : 'dark';
    });
  }, [resolvedTheme]);

  const value = {
    theme: resolvedTheme, // The actual theme being used
    themePreference, // The user's preference setting
    setTheme,
    toggleTheme,
    isDark: resolvedTheme === 'dark',
    isLight: resolvedTheme === 'light',
    isSystem: themePreference === 'system',
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

/**
 * Hook to access theme context
 */
export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}

export default ThemeContext;
